import type { ProductStatus, PublishBlocker } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLatestEditAreaQuery } from '@/entities/edit-area';
import {
  BLOCKER_LABELS,
  useInvalidateProducts,
  useInvalidateRecommendedProducts,
} from '@/entities/product';
import { resetSettledRequests } from '@/shared/lib';
import { Button, cn } from '@/shared/ui';
import { useDeleteDraft } from '../model/use-delete-draft';
import { useHideProduct } from '../model/use-hide-product';
import { usePublishProduct } from '../model/use-publish-product';
import { PreviewNoticeDialog } from './preview-notice-dialog';

interface PublishPanelProps {
  readonly productId: string;
  readonly productName: string;
  readonly status: ProductStatus;
  readonly hasCategories: boolean;
  readonly hasPriceTiers: boolean;
  /** 상품 실측(width_mm·height_mm) 이 둘 다 있는가 — 발행 가드의 NO_MEASUREMENT 조건. */
  readonly hasMeasurement: boolean;
}

const STATUS_BADGES: Record<ProductStatus, { label: string; className: string }> = {
  DRAFT: { label: '등록 중', className: 'bg-muted text-muted-foreground' },
  PUBLISHED: { label: '판매중', className: 'bg-foreground text-background' },
  HIDDEN: { label: '숨김', className: 'bg-destructive/10 text-destructive' },
};

/**
 * 에디터 상단 바 — 상태 배지·상품명·미리보기·판매 활성화 토글·DRAFT 삭제.
 * 토글: 켜기 = 발행(가드 검증), 끄기 = 숨김. DRAFT 로의 역전이는 없다.
 */
export const PublishPanel = ({
  productId,
  productName,
  status,
  hasCategories,
  hasPriceTiers,
  hasMeasurement,
}: PublishPanelProps): ReactElement => {
  const navigate = useNavigate();
  const invalidate = useInvalidateProducts();
  const invalidateProduct = (): void => {
    invalidate.detail(productId);
    invalidate.list();
  };
  // 숨김은 서버에서 추천 순위를 비운다 — 랜딩 화면의 추천 목록도 함께 무효화한다.
  const invalidateRecommended = useInvalidateRecommendedProducts();

  // 편집 영역 존재 여부 — 발행 조건은 아니다. 없으면 인쇄 없는 상품으로 공개된다는 안내 한 줄에만 쓴다.
  const editAreaQuery = useLatestEditAreaQuery(productId);
  // 조회가 실패하면 있는지 없는지 모른다 — "편집영역 없음"이라고 단정하지 않는다.
  const editAreaModeText = editAreaQuery.isPending
    ? '…'
    : editAreaQuery.isError
      ? '편집영역 정보를 불러오지 못했습니다'
      : editAreaQuery.data
        ? '편집영역 있음 — 고객이 에디터에서 디자인한 뒤 견적을 요청합니다'
        : '편집영역 없음 — 인쇄 없는 상품으로 공개되며 고객이 상세에서 바로 견적을 요청합니다';

  const publishAction = usePublishProduct({ onSuccess: invalidateProduct });
  const hideAction = useHideProduct({
    onSuccess: () => {
      invalidateProduct();
      invalidateRecommended();
    },
  });
  const deleteAction = useDeleteDraft({ onSuccess: () => navigate('/products') });

  const [blockers, setBlockers] = useState<readonly PublishBlocker[] | null>(null);
  const [isPreviewNoticeOpen, setIsPreviewNoticeOpen] = useState(false);

  const mutationError = publishAction.error ?? hideAction.error ?? deleteAction.error;
  const badge = STATUS_BADGES[status];
  const isActive = status === 'PUBLISHED';
  const isToggling = publishAction.isPending || hideAction.isPending;

  const guardConditions: readonly { readonly label: string; readonly met: boolean }[] = [
    { label: '소분류 연결', met: hasCategories },
    { label: '가격 구간', met: hasPriceTiers },
    { label: '실측 규격', met: hasMeasurement },
  ];

  // 발행·숨김·삭제는 오류 한 줄(mutationError)을 함께 쓴다. 새 요청을 보내기 전에 세 요청의
  // 지난 오류를 모두 지운다 — 아니면 다른 요청을 해도 옛 오류가 그 요청이 다시 돌 때까지 남는다.
  // 스위치와 삭제 버튼은 서로의 요청 중에도 눌려 다른 요청이 진행 중일 수 있다 — 진행 중인 요청은 지우지 않는다.
  const clearMutationErrors = (): void => {
    resetSettledRequests([publishAction, hideAction, deleteAction]);
  };

  const handleToggle = async (): Promise<void> => {
    clearMutationErrors();

    if (isActive) {
      hideAction.hide(productId);
      return;
    }

    setBlockers(null);

    const result = await publishAction.publish(productId);

    if (result && !result.published) {
      setBlockers(result.unmetConditions);
    }
  };

  const handleDelete = (): void => {
    if (window.confirm(`등록 중인 상품 "${productName}" 을(를) 삭제하시겠습니까?`)) {
      clearMutationErrors();
      deleteAction.deleteDraft(productId);
    }
  };

  return (
    <section className="rounded-2xl border border-foreground/[0.06] bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-3">
          <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', badge.className)}>
            {badge.label}
          </span>
          <h1 className="text-xl font-semibold">{productName}</h1>
        </span>

        <span className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setIsPreviewNoticeOpen(true)}>
            상품 미리보기
          </Button>

          <label className="flex items-center gap-2 text-sm">
            판매 활성화
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              aria-label="판매 활성화"
              disabled={isToggling}
              onClick={() => void handleToggle()}
              className={cn(
                'relative h-6 w-11 rounded-full transition-colors disabled:opacity-50',
                isActive ? 'bg-primary' : 'bg-muted-foreground/30',
              )}
            >
              <span
                className={cn(
                  'absolute left-0.5 top-0.5 size-5 rounded-full bg-background shadow transition-transform',
                  isActive && 'translate-x-5',
                )}
              />
            </button>
          </label>

          {status === 'DRAFT' ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              isLoading={deleteAction.isPending}
              onClick={handleDelete}
            >
              삭제
            </Button>
          ) : null}
        </span>
      </div>

      <p data-testid="edit-area-mode" className="mt-3 text-sm text-muted-foreground">
        {editAreaModeText}
      </p>

      {status !== 'PUBLISHED' ? (
        <div className="mt-3 text-sm">
          <span className="text-muted-foreground">발행 조건: </span>
          <ul className="mt-1 inline-flex flex-wrap gap-x-4 gap-y-1">
            {guardConditions.map((condition) => (
              <li
                key={condition.label}
                className={
                  condition.met === false ? 'font-medium text-destructive' : 'text-muted-foreground'
                }
              >
                {condition.met ? '✓ ' : '✗ '}
                {condition.label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {mutationError ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {mutationError.message}
        </p>
      ) : null}

      {blockers ? (
        <div role="alert" className="mt-3 rounded-lg border border-destructive/40 p-3 text-sm">
          판매를 활성화하려면 다음 조건을 먼저 채워야 합니다:
          <ul className="mt-1 list-inside list-disc">
            {blockers.map((blocker) => (
              <li key={blocker}>{BLOCKER_LABELS[blocker]}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {isPreviewNoticeOpen ? (
        <PreviewNoticeDialog onClose={() => setIsPreviewNoticeOpen(false)} />
      ) : null}
    </section>
  );
};
